/**
 * Automatic Version Sync – Client-Side Handler
 *
 * This module ensures that the frontend (React) automatically reloads itself
 * when a newer backward-compatible backend version is detected.
 *
 * ## How It Works:
 * - The server (FastAPI) injects an `X-Api-Version` header into every response.
 * - The frontend build embeds its own version as `process.env.REACT_APP_VERSION`.
 * - An Axios response interceptor compares versions:
 *   - If both are valid SemVer strings:
 *     - Reloads the page if MAJOR versions differ or server's MINOR is newer.
 *     - Ignores non-SemVer strings (e.g. "dev", Git hashes).
 *
 * ## Benefits:
 * - Zero additional HTTP traffic.
 * - Fast propagation of compatible backend updates.
 * - No user prompts: reload is silent and instant.
 * - Safe for multiple open tabs.
 */

const SEMVER_RE = /^v?(\d+)\.(\d+)\.(\d+)$/;

export const parseSemver = (v: string): [number, number, number] | null => {
    const m = v.match(SEMVER_RE);
    if (!m) return null;
    return [Number(m[1]), Number(m[2]), Number(m[3])];
};

export const shouldReload = (
    serverVersion: string | null,
    clientVersion: string | undefined
): boolean => {
    if (!serverVersion || !clientVersion) return false;

    const serverSemver = parseSemver(serverVersion);
    const clientSemver = parseSemver(clientVersion);
    if (!serverSemver || !clientSemver) return false;

    const [serverMajor, serverMinor] = serverSemver;
    const [clientMajor, clientMinor] = clientSemver;

    if (serverMajor !== clientMajor) return true;
    return serverMinor > clientMinor;
};
